import type { FC } from 'react';
import type { TFunction } from 'i18next';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Button, Flex, Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import { Text } from '@shared/ui/antd';

import { DeleteOutlinedSmall, PlusBackground } from '@shared/assets/icons/components';

import styles from '../../prompt.module.scss';

interface AdditionalInstructionsProps {
  ruleFields: { id: string }[];
  exceptionFields: { id: string }[];
  control: Control<UpdateFormValues>;
  errors: FieldErrors<UpdateFormValues>;
  isLoading: boolean;
  onAddRule: () => void;
  onRemoveRule: (index: number) => void;
  onAddException: () => void;
  onRemoveException: (index: number) => void;
  rulePlaceholders: string[];
  exceptionPlaceholders: string[];
  t: TFunction<'translation', 'prompt'>;
}

export const AdditionalInstructions: FC<AdditionalInstructionsProps> = ({
  ruleFields,
  exceptionFields,
  control,
  errors,
  isLoading,
  onAddRule,
  onRemoveRule,
  onAddException,
  onRemoveException,
  rulePlaceholders,
  exceptionPlaceholders,
  t,
}) => (
  <Flex vertical className={styles.element} gap={16}>
    <Text variant="lg" fontWeight={500}>
      {t('additionalInstruction')}
    </Text>

    <Flex vertical className={styles.element_inside} gap={12}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('rules')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('rulesTooltip')}
        </Text>
      </Flex>

      <Flex vertical gap={12} style={{ width: '100%' }}>
        {ruleFields.map((field, index) => (
          <Flex key={field.id} align="start" gap={12}>
            <Form.Item
              name={`rules.${index}`}
              help={errors.rules?.[index]?.message}
              validateStatus={errors.rules?.[index] ? 'error' : ''}
              className={styles.label}
            >
              <Controller
                control={control}
                name={`rules.${index}`}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    id={`rules-${index}`}
                    placeholder={rulePlaceholders[index] ?? rulePlaceholders.at(-1)}
                    className={styles.area}
                    style={{ resize: 'none' }}
                    showCount
                    autoSize={{ minRows: 3 }}
                    disabled={isLoading}
                  />
                )}
              />
            </Form.Item>

            <button
              type="button"
              className={styles.stageActionButton}
              onClick={() => onRemoveRule(index)}
              disabled={ruleFields.length <= 1}
              aria-label="Remove stage"
            >
              <DeleteOutlinedSmall color="#A7A8AE" />
            </button>
          </Flex>
        ))}
      </Flex>

      <Button
        icon={<PlusBackground fill="var(--color-text-secondary)" />}
        iconPosition="end"
        onClick={onAddRule}
        type="text"
        className={styles.button_additional}
      >
        {t('addRule')}
      </Button>
    </Flex>

    <Flex vertical className={styles.element_inside} gap={12}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('exceptions')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('exceiptionsTooltip')}
        </Text>
      </Flex>

      <Flex vertical gap={12} style={{ width: '100%' }}>
        {exceptionFields.map((field, index) => (
          <Flex key={field.id} align="start" gap={12}>
            <Form.Item
              name={`exceptions.${index}`}
              help={errors.exceptions?.[index]?.message}
              validateStatus={errors.exceptions?.[index] ? 'error' : ''}
              className={styles.label}
            >
              <Controller
                control={control}
                name={`exceptions.${index}`}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    id={`exceptions-${index}`}
                    placeholder={exceptionPlaceholders[index] ?? exceptionPlaceholders.at(-1)}
                    className={styles.area}
                    style={{ resize: 'none' }}
                    showCount
                    autoSize={{ minRows: 3 }}
                    disabled={isLoading}
                  />
                )}
              />
            </Form.Item>

            <button
              type="button"
              className={styles.stageActionButton}
              onClick={() => onRemoveException(index)}
              disabled={exceptionFields.length <= 1}
              aria-label="Remove stage"
            >
              <DeleteOutlinedSmall color="#A7A8AE" />
            </button>
          </Flex>
        ))}
      </Flex>

      <Button
        icon={<PlusBackground fill="var(--color-text-secondary)" />}
        iconPosition="end"
        onClick={onAddException}
        type="text"
        className={styles.button_additional}
      >
        {t('addException')}
      </Button>
    </Flex>
  </Flex>
);
